import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { CheckBatchStatus } from '@/types/check';

/**
 * checkBatchStore — 素材批次檢查進度(R209;慣例照 activeJobStore 全套)。
 * -----------------------------------------------------------------------------
 * 資料流:UI 呼叫 `services.checkService.startBatch` → `subscribeBatch` 把進度
 * 事件寫進本 store(fixture = timer 推;BE 上線後 = Firestore listener 推,
 * store shape 不變)→ 素材庫清單「檢查結果」欄 / 頁內進度條 / 跨頁 minibar
 * 各自以 selector 訂閱。
 *
 * 訂閱紀律(panel 條款,防大批次逐筆寫回全表重繪):
 *   - 列 badge selector = `s.batch?.results[key]`(進度 tick 不動已寫入 entry 的
 *     reference → 未變列 Object.is 擋 re-render)
 *   - live 態 selector 回 primitive;`step` 絕不進列 selector
 *   - 「已執行秒數」由 `startedAt` 在進度條本地派生,**不進 store**(否則每秒
 *     寫 localStorage + 全訂閱者重渲)
 *
 * persist:
 *   - key `hai.checkBatch`(repo 前綴;D3_ 是設計稿 mock 專用 — G-8 #4)
 *   - 只存 badge 態/計數/複合鍵/時間戳 — **簽 URL 與報告本體不得進 localStorage**
 *   - `minibarClosed` 是 per-session UI → partialize 排除
 *   - TTL 30min(startedAt 基準)+ module-load prune;登出走 sessionReset `clearAll`
 *     (漏做 = 共用電腦換帳號殘留他人批次進度 — 紅線非選配)
 */

export const CHECK_BATCH_PERSIST_VERSION = 1;
export const CHECK_BATCH_TTL_MS = 30 * 60 * 1000; // 30 min

export interface CheckBatchStoreState {
  batch: CheckBatchStatus | null;
  /** minibar 手動關閉(新批次啟動時重置)。 */
  minibarClosed: boolean;
  /** listener / cold-start 寫入(整包覆蓋;新 batchId 自動重置 minibarClosed)。 */
  setBatch(status: CheckBatchStatus): void;
  /**
   * 局部更新(R212;Firestore **兩路**推播用)。
   *
   * job doc 那路只帶計數/進度、items 子集合那路只帶 `results`/`reportIds` ——
   * 各自 patch 才不會互相把對方的欄位覆蓋成空(docs/88 §2.3:job doc 沒有 items)。
   * `results` 傳進來時**逐 key 併入**:未變動的 key 保留原 reference,維持本 store
   * per-row `Object.is` 的 memo 紀律。無 batch 時 no-op(不憑空造 batch)。
   */
  patchBatch(patch: Partial<CheckBatchStatus>): void;
  closeMinibar(): void;
  /** 清掉批次(取消後手動收、或 getBatch 回 null 清 stale)。 */
  clearBatch(): void;
  /** 登出清空(sessionReset 單一出口)。 */
  clearAll(): void;
  /** TTL prune(module load 跑一次)。 */
  pruneStale(now?: number): void;
}

export const useCheckBatchStore = create<CheckBatchStoreState>()(
  persist(
    (set, get) => ({
      batch: null,
      minibarClosed: false,
      setBatch: (status) =>
        set((state) => ({
          batch: status,
          minibarClosed: state.batch?.batchId === status.batchId ? state.minibarClosed : false,
        })),
      patchBatch: (patch) =>
        set((state) => {
          const cur = state.batch;
          if (!cur) return state; // 沒有 active batch → 推播不憑空造一個
          let results = cur.results;
          if (patch.results) {
            // 逐 key 併入;值相同(同一 badge 物件)就沿用舊 reference
            const next = { ...cur.results };
            let changed = false;
            for (const [k, v] of Object.entries(patch.results)) {
              if (next[k] !== v) {
                next[k] = v;
                changed = true;
              }
            }
            if (changed) results = next;
          }
          return { batch: { ...cur, ...patch, results } };
        }),
      closeMinibar: () => set({ minibarClosed: true }),
      clearBatch: () => set({ batch: null, minibarClosed: false }),
      clearAll: () => set({ batch: null, minibarClosed: false }),
      pruneStale: (now = Date.now()) => {
        const b = get().batch;
        if (b && now - b.startedAt >= CHECK_BATCH_TTL_MS) {
          set({ batch: null, minibarClosed: false });
        }
      },
    }),
    {
      name: 'hai.checkBatch',
      version: CHECK_BATCH_PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ batch: state.batch }) as Partial<CheckBatchStoreState>,
      migrate: (persistedState: unknown, _version: number) => {
        // v1 起點;未來 shape 變動時 bump version + 在此補真 migration
        const state = persistedState as Partial<CheckBatchStoreState> | undefined;
        return { batch: state?.batch ?? null } as Partial<CheckBatchStoreState>;
      },
    },
  ),
);

// module load 跑一次 prune(createJSONStorage(localStorage) 同步 rehydrate,
// 此時已完成 — 同 activeJobStore.ts:243-245 的前提註記)。
useCheckBatchStore.getState().pruneStale();

// R210 — minibar 跨 tab 同步(確認清單 G-4 P2 加分項):zustand persist rehydrate
// 不會自動跨 tab,別分頁的批次寫入 localStorage 後以 storage 事件觸發本 tab 重讀。
// storage 事件只在**其他** tab 寫入時觸發(本 tab 寫入不迴圈)。
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'hai.checkBatch') {
      void useCheckBatchStore.persist.rehydrate();
    }
  });
}
